import config from "config";
import { SectionTitle } from "pages/Main/SectionTitle";
import React, { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { mediaMin } from "styles/media";

const FluidWrapper = styled.section`
  width: 100%;
  background-color: ${({ theme }) => theme.palette.gray80};
  overflow: hidden;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px 32px 80px;

  ${mediaMin.xs`
    padding: 140px 32px 80px;
  `}

  ${mediaMin.md`
    padding: 200px 32px 80px;
  `}
`;

const SectionTitleWrapper = styled.div`
  margin-bottom: 60px;
`;

const StyledSectionTitle = styled(SectionTitle)`
  ${mediaMin.xs`
    font-size: 44px;
  `}

  ${mediaMin.sm`
    font-size: 48px;
  `}

  ${mediaMin.md`
    font-size: 52px;
  `}

  ${mediaMin.lg`
    font-size: 58px;
  `}
`;

const SectionDescription = styled.div`
  font-size: 18px;
  text-align: center;
  color: ${({ theme }) => theme.palette.black};
  margin-top: 22px;
  line-height: 1.4;

  ${mediaMin.sm`
    font-size: 20px;
  `}
`;

const VideoWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;

  ${mediaMin.sm`
    max-width: 760px;
  `}

  ${mediaMin.lg`
    max-width: 944px;
  `}
`;

const Video = styled.video`
  width: 100%;
`;

export function ClientExperienceSection() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollTop = window.pageYOffset;

      if (wrapperRef.current && contentRef.current && videoWrapperRef.current) {
        const wrapperOffsetTop = wrapperRef.current.offsetTop;
        const startScrollTop = wrapperOffsetTop - window.innerHeight - 64;
        if (currentScrollTop < startScrollTop) {
          contentRef.current.style.transform = `translateY(-500px)`;
          videoWrapperRef.current.style.transform = `scale(2)`;
        } else if (
          currentScrollTop >= startScrollTop &&
          currentScrollTop < startScrollTop + window.innerHeight
        ) {
          const scrollRatio =
            (currentScrollTop - startScrollTop) / window.innerHeight;
          contentRef.current.style.transform = `translateY(-${
            500 * (1 - scrollRatio)
          }px)`;
          videoWrapperRef.current.style.transform = `scale(${
            1 + (1 - scrollRatio)
          })`;
        } else {
          contentRef.current.style.transform = "";
          videoWrapperRef.current.style.transform = "";
        }
      }
    };
    document.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <FluidWrapper ref={wrapperRef}>
      <Content ref={contentRef}>
        <SectionTitleWrapper>
          <StyledSectionTitle color="black">
            <Trans i18nKey="main.clientExperienceSection.mainCopy" />
          </StyledSectionTitle>
          <SectionDescription>
            {t("main.clientExperienceSection.subCopy")}
          </SectionDescription>
        </SectionTitleWrapper>
        <VideoWrapper ref={videoWrapperRef}>
          <Video
            src={`${config.RESOURCE_ORIGIN}/videos/landing/main/client-benefit-1.mp4`}
            autoPlay
            muted
            loop
            playsInline
          />
        </VideoWrapper>
      </Content>
    </FluidWrapper>
  );
}
